import { invoke } from '~/lib/tauriInvoke';
import * as Sentry from '@sentry/nuxt';
import { loadAppSettings, patchAppSettings } from '~/lib/settings/appSettings';
import { BrowserUnsupportedCommandError } from '~/lib/platform/ClientSdk';

export enum SoundType {
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  MESSAGE = 'message',
  SCREEN_SHARE_START = 'screen_share_start',
  SCREEN_SHARE_STOP = 'screen_share_stop',
  RECONNECTING = 'reconnecting',
  MUTED = 'muted',
  NON_MUTED = 'non-muted',
  DEAFENED = 'deaf',
  NON_DEAFENED = 'non-deaf',
}

class SoundManager {
  private volume: number = 1;
  private loopingSound: SoundType | null = null;

  constructor() {
    this.volume = loadAppSettings().sounds.volume;
  }

  /** Persisted separately from `audio.speakerVolume` — this only affects UI/notification sound effects, not call audio. */
  setVolume(volume: number) {
    this.volume = Math.min(1, Math.max(0, volume));
    patchAppSettings({ sounds: { volume: this.volume } });
  }

  private isEnabled(sound: SoundType): boolean {
    const sounds = loadAppSettings().sounds;
    switch (sound) {
      case SoundType.MESSAGE: return sounds.messageSound;
      case SoundType.CONNECT: return sounds.callConnectSound;
      case SoundType.DISCONNECT: return sounds.callDisconnectSound;
      case SoundType.SCREEN_SHARE_START:
      case SoundType.SCREEN_SHARE_STOP: return sounds.screenShareSound;
      // Reconnecting is a connectivity-loss indicator, not a togglable notification sound.
      case SoundType.RECONNECTING: return true;
      // No dedicated toggle for these yet — always on, like RECONNECTING.
      case SoundType.MUTED:
      case SoundType.NON_MUTED:
      case SoundType.DEAFENED:
      case SoundType.NON_DEAFENED: return true;
    }
  }

  async play(sound: SoundType) {
    if (typeof window === 'undefined') return;
    if (!this.isEnabled(sound)) return;

    try {
      await invoke('play_sound', { name: sound, volume: this.volume });
    } catch (e) {
      if (e instanceof BrowserUnsupportedCommandError) return;
      console.error(`[SoundManager] Failed to play sound ${sound}:`, e);
      Sentry.captureException(e, { level: 'warning', extra: { action: 'play_sound', sound } });
    }
  }

  /** Loops `sound` until `stopLoop()` is called; only one loop can play at a time. */
  async startLoop(sound: SoundType) {
    if (typeof window === 'undefined') return;
    this.loopingSound = sound;

    try {
      await invoke('play_loop_sound', { name: sound, volume: this.volume });
    } catch (e) {
      if (e instanceof BrowserUnsupportedCommandError) return;
      console.error(`[SoundManager] Failed to start loop sound ${sound}:`, e);
      Sentry.captureException(e, { level: 'warning', extra: { action: 'play_loop_sound', sound } });
    }
  }

  async stopLoop() {
    if (typeof window === 'undefined' || !this.loopingSound) return;
    this.loopingSound = null;

    try {
      await invoke('stop_loop_sound');
    } catch (e) {
      if (e instanceof BrowserUnsupportedCommandError) return;
      console.error('[SoundManager] Failed to stop loop sound:', e);
      Sentry.captureException(e, { level: 'warning', extra: { action: 'stop_loop_sound' } });
    }
  }
}

export const soundManager = new SoundManager();
